import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { Tradable } from "@models/interfaces/tradable";
import { config, externalPaths } from "@config";
import { TranslationService } from "@core-services/translation.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Cryptocoin, SwapOfferModel, Validateable, Wallet } from "@models";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { StockToggleSlider } from "@models/stock-toggles";
import { TOKEN_BEST_ID } from "@helpers/constants";
import { MasterdataService } from "@core-services/masterdata.service";
import { Index } from "@models/asset/index/index.model";
import { Warning } from "@models/trade-offer.model";
import { Metal } from "@models/asset/metal/metal";
import { BehaviorSubject, combineLatest } from "rxjs";
import { map } from "rxjs/operators";

@Component({
	selector: "swap-toggles",
	templateUrl: "./swap-toggles.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SwapTogglesComponent {
	@Input() public leftSelectionWallet: Wallet = null;
	@Input() public rightSelectionWallet: Wallet = null;

	@Input() public swapOfferAPIData: SwapOfferModel = new SwapOfferModel();
	@Input() public apiPending: boolean = false;

	@Input() public showAppTestWarning = false;

	@Output() public sliderValueUpdate: EventEmitter<boolean> = new EventEmitter<boolean>();

	public swapLockSubject$: BehaviorSubject<SwapLock> = new BehaviorSubject<SwapLock>(new SwapLock());
	public stockTermsTogglesSubject$: BehaviorSubject<StockToggleSlider> = new BehaviorSubject<StockToggleSlider>(
		new StockToggleSlider()
	);
	public combinedTogglesObservables$ = combineLatest([this.swapLockSubject$, this.stockTermsTogglesSubject$]).pipe(
		map(([swapLock, stockTermsToggle]) => ({ swapLock, stockTermsToggle }))
	);

	constructor(
		private translationService: TranslationService,
		private tradableListService: TradableListService,
		private masterDataService: MasterdataService
	) {}

	public sliderChange(): void {
		this.sliderValueUpdate.emit();
	}

	public getSwapLock(): SwapLock {
		return this.swapLockSubject$.getValue();
	}

	public getStockToggleTerms(): StockToggleSlider {
		return this.stockTermsTogglesSubject$.getValue();
	}

	public disableSliders(): void {
		this.swapLockSubject$.next(new SwapLock());
		this.stockTermsTogglesSubject$.next(new StockToggleSlider());
	}

	public validateSliders(): void {
		const swapLock = this.getSwapLock();
		if (this.isLocked()) {
			if (!swapLock.enabled) {
				swapLock.wiggle("enabled");
				this.swapLockSubject$.next(swapLock);
			}

			if (!swapLock.index_terms) {
				swapLock.wiggle("index_terms");
				this.swapLockSubject$.next(swapLock);
			}
			if (this.hasStockTerms() && !this.getStockToggleTerms().getAllEnabled()) {
				this.getStockToggleTerms().wiggleAll();
			}
			if (!swapLock.stock_risk) {
				swapLock.wiggle("stock_risk");
				this.swapLockSubject$.next(swapLock);
			}
			if (this.showBest2Terms() && !swapLock.best2_terms) {
				swapLock.wiggle("best2_terms");
				this.swapLockSubject$.next(swapLock);
				return;
			}
		}
		if (this.holdLock) {
			swapLock.wiggle("enabled");
			this.swapLockSubject$.next(swapLock);
			return;
		}

		if (!!this.metalLock && !swapLock.metal_terms) {
			swapLock.wiggle("metal_terms");
			this.swapLockSubject$.next(swapLock);
			return;
		}
	}

	public get metalLock(): boolean {
		let isMetal: boolean = false;
		if (this.rightSelectionWallet) {
			isMetal = this.swapIntoMetal();
		}
		if (!isMetal) {
			return this.isLocked();
		}
		return this.isLocked() || !this.getSwapLock().metal_terms;
	}

	public swapIntoMetal(): boolean {
		return this.getWalletTradable(this.rightSelectionWallet) instanceof Metal;
	}

	public getHodlLink(): string {
		return this.translationService.getHdLink() + config.externalPaths["tradeOffer"]["hodlLink"];
	}

	public getMetalsLink(): string {
		const lang = TranslationService.getLanguageIsoCode();
		return (config.baseUrl + config.externalPaths["commodities"]["storageFeesLink"]).replace("%s", lang);
	}

	public getIndexTermsLink(): string {
		return (config.baseUrl + config.externalPaths["index"]["prospectus"]).replace(
			"%s",
			TranslationService.getLanguageIsoCode()
		);
	}
	public getWalletTradable(wallet: Wallet): Tradable {
		let currency: Tradable;
		if (wallet instanceof TradableWallet) {
			const tradableId = wallet.getCurrencyId();
			currency = this.tradableListService.getItemById(tradableId);
		}
		return currency;
	}

	public isDerivative(wallet: Wallet): boolean {
		const currency: Tradable = this.getWalletTradable(wallet);
		return !!currency && currency.isDerivative();
	}

	public hasStockTerms(): boolean {
		if (!this.rightSelectionWallet || !this.leftSelectionWallet) {
			return false;
		}
		return this.isDerivative(this.rightSelectionWallet) || this.isDerivative(this.leftSelectionWallet);
	}

	/*
		Regular terms (crypto) should be hidden when both dropdowns have derivatives selected
	 */
	public showRegularTerms(): boolean {
		if (!this.hasStockTerms()) {
			return true;
		}
		return !this.bothSelectedDerivatives();
	}

	// Both dropdowns have derivatives as the selected values
	public bothSelectedDerivatives(): boolean {
		if (!this.hasStockTerms()) {
			return false;
		}
		return this.isDerivative(this.rightSelectionWallet) && this.isDerivative(this.leftSelectionWallet);
	}

	// Regular crypto terms and Stocks terms
	private regularTermsStockAccepted(): boolean {
		if (this.bothSelectedDerivatives()) {
			return this.getStockToggleTerms().getAllEnabled();
		}

		return this.hasStockTerms()
			? this.getStockToggleTerms().getAllEnabled() && this.getSwapLock().enabled && !this.apiPending
			: this.getSwapLock().enabled && !this.apiPending;
	}

	private isLocked(): boolean {
		return (
			!this.regularTermsStockAccepted() ||
			this.isSameCurrency() ||
			(this.hasIndexSlider() && !this.getSwapLock().index_terms) ||
			(this.showStockRiskToggle() && !this.getSwapLock().stock_risk) ||
			(this.showBest2Terms() && !this.getSwapLock().best2_terms)
		);
	}

	public isSameCurrency(): boolean {
		const first = this.leftSelectionWallet;
		const second = this.rightSelectionWallet;
		if (!!first && !!second) {
			return first.getCurrencyId() === second.getCurrencyId();
		}
		return false;
	}

	public showBest2Terms(): boolean {
		return (
			this.masterDataService.isBest2ProductLive() && this.rightSelectionWallet?.getCurrencyId() === TOKEN_BEST_ID
		);
	}

	public getBest2TermsLink(): string {
		const language: string = TranslationService.getLanguageIsoCode() === "de" ? "de" : "en";
		return `${config.baseUrl}${language}${externalPaths.best.best_two_legal}`;
	}

	public buyingHoldTradable(): boolean {
		return this.getTradable(this.rightSelectionWallet).isHodlTradable("buy");
	}

	public isIndex(wallet: Wallet): boolean {
		const currency: Tradable = this.getWalletTradable(wallet);
		return !!currency && currency instanceof Index;
	}

	public isRightDropdownIndex(): boolean {
		return this.isIndex(this.rightSelectionWallet);
	}

	public hasIndexSlider(): boolean {
		return this.isRightDropdownIndex();
	}

	public showTradingHoursWarning(): boolean {
		return this.swapOfferAPIData.showTradingHoursWarning();
	}

	public getStockWarningMessage(): Warning[] {
		return this.swapOfferAPIData.getStockWarnings();
	}

	public getWarningAction(): string {
		if (!this.hasStockTerms()) {
			return "";
		}
		return "swap";
	}

	public getStockToggleTradable(): Tradable {
		if (this.isDerivative(this.rightSelectionWallet)) {
			return this.getTradable(this.rightSelectionWallet);
		}
		if (this.isDerivative(this.leftSelectionWallet)) {
			return this.getTradable(this.leftSelectionWallet);
		}
		return null; // Default
	}

	public showStockRiskToggle(): boolean {
		if (!this.rightSelectionWallet) {
			return false;
		}

		return this.showAppTestWarning && this.isDerivative(this.rightSelectionWallet);
	}

	private getTradable(wallet: Wallet): Tradable {
		if (!wallet) {
			return new Cryptocoin();
		}
		return this.tradableListService.getItemById(wallet.getCurrencyId()) || new Cryptocoin();
	}

	public getHoldInfo(): string {
		const holdTradable: Tradable = this.getHoldTradable();
		return holdTradable?.getInfo() ?? "";
	}

	private getHoldTradable(): Tradable {
		const rTradable: Tradable = this.getTradable(this.rightSelectionWallet);
		if (rTradable.isHodlTradable("buy")) {
			return rTradable;
		}
		return null;
	}

	public get holdLock(): boolean {
		return this.getHoldTradable() ? !this.getSwapLock().hold_coin_terms : false;
	}

	public getIndexName(): string {
		const index: Tradable = this.getWalletTradable(this.rightSelectionWallet);
		return index?.getName();
	}

	public isButtonDisabled(): boolean {
		if (this.swapIntoMetal()) {
			return !this.getSwapLock().enabled;
		}
		return this.metalLock || this.holdLock;
	}
}

export class SwapLock extends Validateable {
	public enabled: boolean = false;
	public metal_terms: boolean = false;
	public index_terms: boolean = false;
	public best2_terms: boolean = false;
	public stock_risk: boolean = false;
	public hold_coin_terms: boolean = false;
}
